import { ActivityIndicatorSettings } from "./types";

export interface AjaxManagerSettings
{
    /** Called when an ajax request starts */
    requestsStarted(status: AjaxManagerStatus): void;
    /** Called when an ajax request is added or completed */
    update: (status: AjaxManagerStatus) => void;
    /** Called when all ajax requests are completed */
    requestsCompleted(status: AjaxManagerStatus): void;

    defaultDuplicateRequestHandling: DUPLICATE_REQUEST_HANDLING;
}

export interface AjaxManagerStatus
{
    numberOfCurrentRequests: number;
    numberOfCompletedRequests: number;
    numberOfTotalRequests: number;
}

export enum DUPLICATE_REQUEST_HANDLING
{
    ABORT_PRIOR = 1,
    ABORT_NEW = 2,
    QUEUE = 3
}

/**
 * The ajax manager ensures that new ajax requests with the same requestID wait for existing ajax requests to finish
 * before firing. This is to prevent race conditions since we can't be certain which order the server will handle the
 * ajax requests. Requests must have a requestID attribute for this to work properly.
 */
export interface AjaxManager
{
    ajax(ajaxSettings: AjaxSettings, managerRequestOptions: Partial<AjaxManagerRequestSettings>): JQuery.jqXHR<any>;
}

export interface AjaxSettings extends JQuery.AjaxSettings
{
    activityIndicator?: ActivityIndicatorSettings;
}

export interface AjaxManagerRequestSettings
{
    /** Used to identify ajax requests, so duplicates can be handled */
    requestID?: string | null;
    duplicateRequestHandling?: DUPLICATE_REQUEST_HANDLING | null;
}

interface CustomJQueryAjaxXHR extends JQuery.jqXHR<any>
{
    _requestID?: string | null;
}

const DEFAULT_AJAX_MANAGER_SETTINGS: AjaxManagerSettings =
{
    requestsStarted: function (/* status object */)
    {
        // Called when an ajax request starts
    },
    update: function (/* status object */)
    {
        // Called when an ajax request is added or completed
    },
    requestsCompleted: function (/* status object */)
    {
        // Called when all ajax requests are completed
    },
    defaultDuplicateRequestHandling: DUPLICATE_REQUEST_HANDLING.ABORT_PRIOR
};

const DEFAULT_AJAX_MANAGER_REQUEST_SETTINGS: AjaxManagerRequestSettings =
{
    requestID: null, // Used to identify ajax requests, so duplicates can be handled
    duplicateRequestHandling: null
};

// The ajax manager ensures that new ajax requests with the same requestID wait for existing ajax requests to finish
// before firing. This is to prevent race conditions since we can't be certain which order the server will handle the
// ajax requests. Requests must have a requestID attribute for this to work properly.
export function createAjaxManager(options?: Partial<AjaxManagerSettings>)
{
    const ajaxManager: AjaxManager =
    {
        ajax
    };
    const settings = $.extend({}, DEFAULT_AJAX_MANAGER_SETTINGS, options) as AjaxManagerSettings;

    const requestTracker = (function ()
    {
        const requestTracker =
        {
            getCurrentRequests,
            add
        };

        let requestsInProgress = false;
        let queue: CustomJQueryAjaxXHR[] = [];
        let requestsComplete = 0;

        return requestTracker;

        function getCurrentRequests()
        {
            const currentRequests = [];

            for (let i = 0; i < queue.length; i++)
            {
                if (queue[i].state() == "pending")
                {
                    currentRequests.push(queue[i]);
                }
            }

            return currentRequests;
        }

        function add(request: CustomJQueryAjaxXHR)
        {
            if (!requestsInProgress)
            {
                requestsInProgress = true;
                settings.requestsStarted(getStatus());
            }

            queue.push(request);

            request.always(function ()
            {
                requestsComplete++;
                settings.update(getStatus());

                if (requestsComplete == queue.length)
                {
                    settings.requestsCompleted(getStatus());
                    reset();
                }
            });
        }

        function reset()
        {
            requestsInProgress = false;
            queue = [];
            requestsComplete = 0;
        }

        function getNumberOfCurrentRequests()
        {
            return requestTracker.getCurrentRequests().length;
        }

        function getStatus()
        {
            const status: AjaxManagerStatus =
            {
                numberOfCurrentRequests: getNumberOfCurrentRequests(),
                numberOfCompletedRequests: requestsComplete,
                numberOfTotalRequests: queue.length
            };

            return status;
        }
    })();

    return ajaxManager;

    function ajax(ajaxSettings: AjaxSettings, managerRequestOptions: Partial<AjaxManagerRequestSettings>)
    {
        const requestSettings = $.extend({}, DEFAULT_AJAX_MANAGER_REQUEST_SETTINGS, managerRequestOptions) as AjaxManagerRequestSettings;

        return performRequest(ajaxSettings, requestSettings);
    }

    function findConflictingRequestsInProgress(requestSettings: AjaxManagerRequestSettings)
    {
        const conflicts: CustomJQueryAjaxXHR[] = [];

        if (requestSettings.requestID == null)
        {
            return conflicts;
        }

        const queue = requestTracker.getCurrentRequests();

        for (let i = 0; i < queue.length; i++)
        {
            if (queue[i]._requestID == requestSettings.requestID)
            {
                conflicts.push(queue[i]);
            }
        }

        return conflicts;
    }

    function performRequest(ajaxSettings: AjaxSettings, requestSettings: AjaxManagerRequestSettings)
    {
        const deferred = $.Deferred();
        let promise;
        const conflictingRequests = findConflictingRequestsInProgress(requestSettings);
        const handleDuplicates = requestSettings.duplicateRequestHandling != null ? requestSettings.duplicateRequestHandling : settings.defaultDuplicateRequestHandling;

        if (handleDuplicates == DUPLICATE_REQUEST_HANDLING.QUEUE)
        {
            promise = createQueuedRequest(conflictingRequests, deferred, ajaxSettings);
        }
        else if (handleDuplicates == DUPLICATE_REQUEST_HANDLING.ABORT_PRIOR)
        {
            promise = fireRequest(deferred, ajaxSettings);
        }
        else
        {
            if (conflictingRequests.length > 0)
            {
                promise = createAbortedRequest(deferred, ajaxSettings);
            }
            else
            {
                promise = fireRequest(deferred, ajaxSettings);
            }
        }

        promise._requestID = requestSettings.requestID;
        requestTracker.add(promise);

        if (handleDuplicates == DUPLICATE_REQUEST_HANDLING.ABORT_PRIOR)
        {
            // Abort after firing off the new request to prevent triggering requestsCompleted() prematurely.
            for (var i = 0; i < conflictingRequests.length; i++)
            {
                conflictingRequests[i].abort();
            }
        }

        return promise;
    }

    function createQueuedRequest(requestsToQueueAfter: CustomJQueryAjaxXHR[], deferred: JQuery.Deferred<any>, ajaxSettings: AjaxSettings)
    {
        const promise = deferred.promise() as CustomJQueryAjaxXHR;
        let aborted = false;

        // This abort function is only temporary. It is only called if abort() is
        // called before the request is actually fired. It later gets replaced by the 
        // abort function created in fireRequest().
        promise.abort = function ()
        {
            aborted = true;
            createAbortedRequest(deferred, ajaxSettings);
        };

        $.when.apply($, requestsToQueueAfter).always(function ()
        {
            if (!aborted)
            {
                fireRequest(deferred, ajaxSettings);
            }
        });

        return promise;
    }

    function createAbortedRequest(deferred: JQuery.Deferred<any>, ajaxSettings: AjaxSettings)
    {
        var modifiedSettings = $.extend({}, ajaxSettings, { beforeSend: function () { return false; } });

        return fireRequest(deferred, modifiedSettings);
    }

    function fireRequest(deferred: JQuery.Deferred<any>, ajaxSettings: AjaxSettings)
    {
        const promise = deferred.promise() as CustomJQueryAjaxXHR;
        const jqXHR = $.ajax(ajaxSettings);

        jqXHR.done(function (this: unknown) { deferred.resolve.apply(this, arguments as any); });
        jqXHR.fail(function (this: unknown) { deferred.reject.apply(this, arguments as any); });

        promise.abort = function ()
        {
            jqXHR.abort();
        };

        return promise;
    }
}